/**
 * Copyright (c) 2023 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import dayjs from 'dayjs';
import { Sanitize } from 'src/DistributionPackages/BestFit.Magicline/Resources/Private/App/Components/Sanitizer';
import './styles.scss';
import InputField
  from 'src/DistributionPackages/BestFit.Magicline/Resources/Private/App/Components/FormFields/InputField';
import SelectField
  from 'src/DistributionPackages/BestFit.Magicline/Resources/Private/App/Components/FormFields/SelectField';
import CheckboxField
  from 'src/DistributionPackages/BestFit.Magicline/Resources/Private/App/Components/FormFields/CheckboxField';
import {
  DatePickerField,
} from 'src/DistributionPackages/BestFit.Magicline/Resources/Private/App/Components/FormFields/DatePickerField';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useUserForm } from './useUserForm';

/**
 * UserForm Component
 * @param props
 * @returns {Element}
 * @constructor
 */
export default function UserForm(props) {
  const { t } = useTranslation();
  const { values } = useFormikContext();
  const clientsTimeZone = dayjs.tz.guess();
  const {
    thirdStepContent, resetValidSteps, formField: {
      firstName, lastName, email, phonePrefix, phone, marketingTerms, generalTerms, gender, dateOfBirth,
    }, marketingConsent, marketingConsentIsRequired, underageResource, privacyPolicyConsent,
  } = props;

  const {
    isUnderage,
  } = useUserForm();

  const handlePrivatePolicyConsentChange = (e) => {
    if (!e.target?.checked) {
      resetValidSteps();
    }
  };
  const handleMarketingConsentChange = (e) => {
    if (!e.target?.checked && marketingConsentIsRequired) {
      resetValidSteps();
    }
  };

  /**
     * Get human-readable due date
     * @returns {string}
     */
  const getHumReadableDueDate = () => {
    const dueDate = values.dueDate.split('+');
    return `${dayjs(dueDate[0])
      .tz(clientsTimeZone)
      .format('DD.MM.YYYY HH:mm')} - ${dayjs(dueDate[1])
      .tz(clientsTimeZone)
      .format('HH:mm')} Uhr`;
  };

  return (
    <Grid container spacing={2} sx={{ mb: 4 }}>
      <Grid item xs={12} sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 'bold', mb: 2,
          }}
        >
          {t('currentChoice')}
        </Typography>
        <Typography variant="subtitle2">
          {t('yourChoice')}
          {' '}
          {values.studioName}
        </Typography>
        <Typography variant="subtitle2">
          {t('trainingForm')}
          {' '}
          {values.formOfTraining === 'true' ? t('withTrainer') : t('withoutTrainer')}
        </Typography>
        <Typography variant="subtitle2">
          {t('desiredDate')}
          {' '}
          {getHumReadableDueDate()}
        </Typography>
      </Grid>

      <Sanitize value={thirdStepContent} />
      <Grid item xs={12}>
        <InputField name={firstName.name} label={firstName.label} fullWidth required />
      </Grid>
      <Grid item xs={12}>
        <InputField name={lastName.name} label={lastName.label} fullWidth required />
      </Grid>
      <Grid item xs={12}>
        <SelectField
          name={gender.name}
          label={gender.label}
          data={gender.data}
          fullWidth
          required
        />
      </Grid>
      <Grid item xs={12}>
        <DatePickerField
          sx={{ width: '100%' }}
          required
          label={dateOfBirth.label}
          name={dateOfBirth.name}
          disableFuture
        />
        {isUnderage ? (
          <Box className="underage">
            <Box className="info">{t('under18')}</Box>
            <Box className="file">
              <a href={underageResource} target="_blank" rel="noreferrer">
                <FileDownloadIcon />
                {t('formUnder18')}
              </a>
            </Box>
            <Box className="info hint">{t('postIn')}</Box>
          </Box>
        ) : null}

      </Grid>
      <Grid item xs={12}>
        <InputField name={email.name} label={email.label} fullWidth required />
      </Grid>
      <Grid item xs={12} sm={6}>
        <SelectField
          name={phonePrefix.name}
          label={phonePrefix.label}
          data={phonePrefix.data}
          fullWidth
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <InputField name={phone.name} label={phone.label} fullWidth required />
      </Grid>
      <Grid item xs={12}>
        <Typography
          variant="h5"
          sx={{
            mt: 3, mb: 2, fontWeight: 'bold',
          }}
        >
          {t('notifications')}
        </Typography>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          {t('confirmation')}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <CheckboxField
          name={generalTerms.name}
          label={<Sanitize value={privacyPolicyConsent} />}
          required
          checkedBefore={values.generalTerms}
          onChange={handlePrivatePolicyConsentChange}
        />
      </Grid>
      <Grid item xs={12}>
        <CheckboxField
          name={marketingTerms.name}
          label={<Sanitize value={marketingConsent} />}
          required={marketingConsentIsRequired}
          checkedBefore={values.marketingTerms}
          onChange={handleMarketingConsentChange}
        />
      </Grid>
    </Grid>
  );
}

UserForm.propTypes = {
  thirdStepContent: PropTypes.object.isRequired,
  resetValidSteps: PropTypes.func.isRequired,
  formField: PropTypes.object.isRequired,
  marketingConsent: PropTypes.string.isRequired,
  marketingConsentIsRequired: PropTypes.bool,
  underageResource: PropTypes.string.isRequired,
  privacyPolicyConsent: PropTypes.string.isRequired,
};

UserForm.defaultProps = {
  marketingConsentIsRequired: false,
};
